import { Component, ComponentFactoryResolver, Input, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { ToolbarSettings } from '@base/classes/BaseClasses';
import { BaseComponent } from '@base/component/BaseComponent';
import { ServiceBagService } from '@base/component/ServiceBag';
import { BaseEntity, BaseState } from '@base/entities/BaseEntities';
import { ScreenService } from '@core/framework/screen.service';
import { EnumApplicationMode, EnumPageDisplayModes } from '@core/framework/sysenums.enum';
import { BaseInjectableComponent } from '@m4u-angular/base/component/BaseInjectableComponent';
import { BottomSheetContentParams, BottomSheetService } from '@m4u-angular/core/framework/bottom-sheet.service';
import { SidebarService } from '@m4u-angular/core/framework/sidebar.service';
import { SysGlobals } from '@m4u-angular/core/framework/sysglobals';
import { PushMessageListComponent } from '@m4u-angular/ui/notifications/push-message-list/push-message-list.component';
import { DxContextMenuComponent } from 'devextreme-angular';

class State extends BaseState {
    ToolbarButtons: any[] = [];
    ToolbarSettings: ToolbarSettings = new ToolbarSettings;
    IsBreadcrumbVisible: boolean;
    IsAllowNotifications: boolean;
    InjectedComponents: Set<string> = new Set<string>();
}

@Component({
    selector: 'm4u-toolbar',
    templateUrl: 'toolbar.component.html',
    styleUrls: ['./toolbar.component.scss']
})
export class ToolbarComponent extends BaseComponent<State> {
    @Input() PageComponentName: string;
    @Input() ToolbarButtonsBefore: TemplateRef<any>;
    @Input() ToolbarButtonsCenter: TemplateRef<any>;
    @Input() ToolbarButtonsAfter: TemplateRef<any>;
    @Input() HideBreadcrumbs: boolean = false;

    @ViewChild('mnuViewModes') protected viewModesMenu: DxContextMenuComponent;
    @ViewChild('dynamicContainer', { read: ViewContainerRef }) containerRef: ViewContainerRef;

    get ShowBreadcrumb() {
        return this.A.AppConfigService.AppConfig.ShowBreadcrumb()
            && this.State.ToolbarSettings?.Page?.DisplayMode === EnumPageDisplayModes.Window
            && !this.State.ToolbarSettings?.Page?.IsShowAsFullScreen()
            && this.A.RouteHistoryService.HistoryLength > 2 && !this.HideBreadcrumbs;
    }

    get ShowTitle() {
        return (!this.A.Screen.IsMobile() && this.State.ToolbarSettings?.Page?.DisplayMode !== EnumPageDisplayModes.BottomSheet)
            || (!this.A.Screen.IsMobile() && ((this.State.ToolbarSettings?.Page?.DisplayMode === EnumPageDisplayModes.Window
                && !this.State.ToolbarSettings?.Page?.ShowAsWindowOnMobile)
                || (this.A.AppConfigService.AppConfig.Mode === EnumApplicationMode.SinglePage
                    && this.State.ToolbarSettings?.Page?.DisplayMode === EnumPageDisplayModes.FullScreen)));
    }

    get ShowTitleOnMobile() {
        return this.State.ToolbarSettings?.Page?.DisplayMode === EnumPageDisplayModes.BottomSheet
            || (this.A.Screen.IsMobile() && (this.State.ToolbarSettings?.Page?.DisplayMode === EnumPageDisplayModes.Dialog
                || (this.State.ToolbarSettings?.Page?.DisplayMode === EnumPageDisplayModes.Window
                    && (this.State.ToolbarSettings?.Page?.ShowAsWindowOnMobile || this.State.ToolbarSettings?.Page?.IsShowAsFullScreen()))));
    }

    constructor(A: ServiceBagService,
        public screen: ScreenService,
        private sidebarService: SidebarService,
        private bottomSheetService: BottomSheetService,
        private componentFactoryResolver: ComponentFactoryResolver) {
        super(A, 'ToolbarComponent', State);
    }

    override OnInitComponent() {
        this.RegisterSubscription(this.A.SysEvents.Toolbar.subscribe((settings: ToolbarSettings) => {
            if (!settings || settings.PageComponentName !== this.PageComponentName) return;

            this.State.ToolbarSettings = { ...this.State.ToolbarSettings, ...settings };
            this.State.IsAllowNotifications = this.A.AppConfigService.AppConfig.IsAllowNotifications
                && this.State.ToolbarSettings?.Page?.DisplayMode == EnumPageDisplayModes.FullScreen;
            
            this.InitInjectedComponents();
            this.DetectChanges();
        }));

        this.RegisterSubscription(this.A.SysEvents.Breadcrumb.subscribe(isVisible => {
            this.State.IsBreadcrumbVisible = isVisible;
        }));
    }

    override OnInitComponentFinished() {
        this.InitInjectedComponents();
    }

    InitInjectedComponents() {
        SysGlobals.InjectableComponentsOptions.forEach(x => {
            if (x.TargetModuleName !== this.State.ToolbarSettings.Page?.ModuleName) {
                return;
            }
            if (x.TargetComponentName && x.TargetComponentName !== this.State.ToolbarSettings.Page.ComponentName) {
                return true;
            }
            if (x.IsVisibleButton && !x.IsVisibleButton(this.State.ToolbarSettings.Page)) {
                return;
            }

            const injectedComponent = `${x.TargetComponentType}-${x.ComponentType?.name}`;
            if(!this.containerRef || this.State.InjectedComponents.has(injectedComponent)) {
                return;
            }

            // Resolve an injectable component
            const componentFactory = this.componentFactoryResolver.resolveComponentFactory(x.ComponentType);
            const componentRef = this.containerRef.createComponent<BaseInjectableComponent<BaseEntity, BaseState>>(componentFactory);
            // Set fields
            componentRef.instance.TargetComponentName = this.State.ToolbarSettings.Page.ComponentName;
            componentRef.instance.TargetAppName = this.State.ToolbarSettings.Page.ModuleName;
            componentRef.instance.EntityType = this.State.ToolbarSettings.Page.EntityType;

            // Handle the injectable component on high levels
            this.State.ToolbarSettings.Page.OnInitInjectableComponent(componentRef.instance);

            if (!this.screen.IsMobile() || x.ShowInToolbarOnMobile) {
                // Add buttons to toolbar
                const buttonSettings = {
                    Icon: x.ButtonIcon,
                    Text: !x.ButtonIcon ? x.ButtonText : undefined,
                    Hint: x.ButtonIcon ? x.ButtonText : undefined,
                    Click: () => {
                        componentRef.instance.BaseAction();
                    }
                };
                this.State.ToolbarButtons.push(buttonSettings);

                // Give buttons settings to ths injectable component
                componentRef.instance.ButtonSettings = buttonSettings;
            } else {
                this.State.ToolbarSettings.Page.ContextMenuDefault.push({
                    icon: x.ButtonIcon,
                    text: x.ButtonText,
                    onClick: () => {
                        componentRef.instance.BaseAction();
                    }
                });
            }

            this.State.InjectedComponents.add(injectedComponent);
        });
    }

    //Determine whether to show the close button icon instead of back
    public IsShowCloseButton() {
        return this.IsStandardApplicationMode() && this.State.ToolbarSettings && this.State.ToolbarSettings.ShowCloseButton;
    }

    public IsShowBackButton(): boolean {
        return this.IsStandardApplicationMode() && this.State.ToolbarSettings && this.State.ToolbarSettings.ShowBackButton;
    }

    IsStandardApplicationMode(): boolean {
        return this.A.AppConfigService.AppConfig.Mode === EnumApplicationMode.Standard;
    }

    OnNavigateHomeOrSwitchMode() {
        if (this.State.ToolbarSettings.OnTitleClick) {
            this.State.ToolbarSettings.OnTitleClick();
        } else if (this.IsShowViewModesButton()) {
            //Switch mode
            this.OnShowViewModes();
        } else {
            //Go to home route
            if (!this.IsStandardApplicationMode() || !this.State.ToolbarSettings.Page
                || this.State.ToolbarSettings.Page.DisplayMode != EnumPageDisplayModes.FullScreen) return;
            this.A.Framework.NavigateDefault();
        }
    }

    ToggleMenu = (e) => {
        this.A.SysEvents.SetMenuPositon();
        e.stopPropagation();
    };

    OnShowPushMessages(e) {
        if (!this.screen.IsMobile()) {
            this.sidebarService.Show('Notifications', this.RC('lblNotifications'), PushMessageListComponent);
            return;
        }

        const contentParams = new BottomSheetContentParams();
        contentParams.Name = 'Notifications';
        contentParams.Title = this.RC('lblNotifications');
        contentParams.Content = PushMessageListComponent;
        this.bottomSheetService.Show(contentParams);
    }

    IsShowViewModesButton() {
        return this.State.ToolbarSettings.ViewModes?.length;
    }

    //Show menu after refresh - since menu items may change
    OnShowViewModes = () => {
        this.viewModesMenu.instance.show();
    };

    //Set the title, and call the attached callback
    OnViewModesMenuClick(e) {
        this.State.ToolbarSettings.Title = e.itemData.text;
        e.itemData.callback();
    }

    OnBack() {
        this.State.ToolbarSettings.BackButtonClick();
    }

    //Close page or navigate back using custom callback
    OnClosePage() {
        this.State.ToolbarSettings.CloseButtonClick();
    }

    HasContent(content: any) {
        return content && content.childElementCount > 0;
    }

    IsShowDesktopBackButton() {
        return this.A.Screen.IsDesktop() && this.State.ToolbarSettings.Page?.DisplayMode !== EnumPageDisplayModes.Window
            || this.A.Screen.IsMobile();
    }

    IsShowWindowBackButton() {
        return this.A.Screen.IsDesktop() && this.State.ToolbarSettings.Page?.DisplayMode === EnumPageDisplayModes.Window;
    }

    HasBreadcrumb() {
        return document.querySelector('.toolbar-group-top m4u-breadcrumb')?.childElementCount > 0;
    }

    //ShowHideToolbarButtons() {
    //    const currentPageSelectorPrefix = `.${this.State.ToolbarSettings.PageComponentName}.page-container`;
    //    const flexibleToolbarButtons = document
    //        .querySelectorAll(`${currentPageSelectorPrefix} .p-toolbar-group-right .flexible-toolbar-button`);
    //    const toolbarButtons = document
    //        .querySelectorAll(`${currentPageSelectorPrefix} .p-toolbar-group-right .flexible-toolbar-buttons toolbar-buttons > *`);
    //    const hiddableElements = [...Array.prototype.slice.call(flexibleToolbarButtons), ...Array.prototype.slice.call(toolbarButtons)];

    //    if (hiddableElements.length === 0) return;

    //    const toolbarLeft = document.querySelector(`${currentPageSelectorPrefix} .p-toolbar-group-left`);
    //    const toolbarLeftWidth = toolbarLeft && toolbarLeft.getBoundingClientRect().width || 0;

    //    const toolbar = document.querySelector(`${currentPageSelectorPrefix} .p-toolbar`);
    //    const toolbarPadding = this.A.Screen.IsMobile() ? 8 : 34;
    //    const toolbarWidth = (toolbar && toolbar.getBoundingClientRect().width - 2 * toolbarPadding) || 0;

    //    const toolbarRight = document.querySelector(`${currentPageSelectorPrefix} .p-toolbar-group-right`);
    //    const toolbarRightWidth = toolbarRight && toolbarRight.getBoundingClientRect().width || 0;

    //    const moreButton = document.querySelector(`${currentPageSelectorPrefix} .p-toolbar .more-button`);
    //    const moreButtonWidth = moreButton && moreButton.getBoundingClientRect().width || 0;

    //    if (toolbarLeftWidth + toolbarRightWidth > toolbarWidth) {
    //        const lastItem = hiddableElements[hiddableElements.length - 1];
    //        const lastItemWidth = lastItem.getBoundingClientRect().width;
    //        lastItem.setAttribute('width', lastItemWidth.toString());

    //        const li = document.createElement("li");
    //        li.appendChild(lastItem);

    //        const contextMenus = document.querySelectorAll('.more-button-context-menu ul');
    //        const currentContextMenu = contextMenus[contextMenus.length - 1];
    //        currentContextMenu.appendChild(li);

    //        this.ShowHideMoreButton();
    //        this.ShowHideToolbarButtons();
    //        return;
    //    }

    //    const moreButtonItems = document.querySelectorAll('.more-button-context-menu ul li > *');
    //    const firstMoreElement = moreButtonItems[0];

    //    if (firstMoreElement) {
    //        const firstMoreElementWidth = +firstMoreElement.getAttribute('width') || 0;
    //        let occupiedWidth = toolbarLeftWidth + toolbarRightWidth + firstMoreElementWidth;
    //        if (moreButtonItems.length == 1) occupiedWidth -= moreButtonWidth;
    //        if (occupiedWidth < toolbarWidth) {
    //            const toolbarButtonsContainer = document
    //                .querySelector(`${currentPageSelectorPrefix} .p-toolbar-group-right .flexible-toolbar-buttons toolbar-buttons`);
    //            toolbarButtonsContainer && toolbarButtonsContainer.appendChild(firstMoreElement);

    //            this.ShowHideMoreButton();
    //            this.ShowHideToolbarButtons();
    //        }
    //    }

    //    this.ShowHideMoreButton();
    //}

    //private ShowHideMoreButton() {
    //    const currentPageSelectorPrefix = `.${this.State.ToolbarSettings.PageComponentName}.page-container`;
    //    const moreButtonContainer = document.querySelector(`${currentPageSelectorPrefix} .p-toolbar .more-button-container`);
    //    if (!moreButtonContainer) return;

    //    if (document.querySelectorAll('.more-button-context-menu ul li > *').length === 0) {
    //        moreButtonContainer.classList.add('m4u-hidden');
    //    } else {
    //        moreButtonContainer.classList.remove('m4u-hidden');
    //    }
    //}
}

